/**
 * <RecomposeSteps> — trois étapes qui se cochent pendant qu'un programme se recompose (molecule).
 *
 * Une roue dit « attendez » ; trois lignes qui se cochent disent CE qu'on fait. Les étapes du
 * milieu se cochent au métronome, la dernière n'attend que `done` — on n'annonce pas un résultat
 * qu'on n'a pas. Quand tout est coché, `onFinished` part, une fois : c'est à ce moment que la page
 * montre le programme recomposé. L'étape en cours se dit en `<WaitingText>`, la seule façon
 * d'attendre de l'app.
 *
 * État d'interface : le rythme des coches vit ici ; ce qui est attendu (`done`) arrive en props.
 *
 * Le métronome est un rythme de lecture (le temps de lire une ligne), pas une durée de transition :
 * il ne vient donc pas de `theme.motion`. Le temps laissé à la dernière coche, lui, précède un
 * changement de page : c'est l'entrée de `theme.motion`. « Réduire les animations » passe tout à
 * zéro — les étapes du milieu se cochent d'un coup, la dernière attend toujours la réponse.
 */
import React, { useEffect, useRef, useState } from "react"
import { View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { useReduceMotion } from "../../hooks/useReduceMotion"

/** Le métronome des étapes du milieu : le temps de lire une ligne. */
const FIRST_MS = 520
const STEP_MS = 720

export type RecomposeStepsProps = {
    steps: string[]
    /** La réponse est là : la dernière étape peut se cocher. */
    done: boolean
    onFinished: () => void
    testID?: string
}

export function RecomposeSteps(props: RecomposeStepsProps) {
    const { steps, done, onFinished, testID } = props
    const [ticked, setTicked] = useState(0)
    const finishedRef = useRef(false)
    const onFinishedRef = useRef(onFinished)
    onFinishedRef.current = onFinished
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    // Lu au moment de la dernière coche : un réglage changé pendant l'attente ne la relance pas.
    const holdMsRef = useRef(0)
    holdMsRef.current = reduceMotion ? 0 : theme.motion.entree

    useEffect(() => {
        if (ticked >= steps.length) return undefined
        if (ticked === steps.length - 1 && !done) return undefined
        const pace = reduceMotion ? 0 : ticked === 0 ? FIRST_MS : STEP_MS
        const timer = setTimeout(() => setTicked((n) => n + 1), pace)
        return () => clearTimeout(timer)
    }, [ticked, done, steps.length, reduceMotion])

    useEffect(() => {
        if (ticked < steps.length || finishedRef.current) return undefined
        finishedRef.current = true
        const timer = setTimeout(() => onFinishedRef.current(), holdMsRef.current)
        return () => clearTimeout(timer)
    }, [ticked, steps.length])

    return (
        <Stack gap="3" testID={testID}>
            {steps.map((step, index) => {
                const isDone = index < ticked
                const isActive = index === ticked
                return (
                    <Row key={step} gap="3" align="center">
                        <View style={styles.dot(isDone)}>
                            {isDone && <Icon name="check" size={14} color="white" />}
                        </View>
                        {isActive ? (
                            <WaitingText size="text-base" weight="bold" numberOfLines={2} align="start">
                                {step}
                            </WaitingText>
                        ) : (
                            <Text size="text-base" color={isDone ? "text-primary" : "text-muted"}>
                                {step}
                            </Text>
                        )}
                    </Row>
                )
            })}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    dot: (done: boolean) => ({
        width: 22,
        height: 22,
        borderRadius: 11,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: done ? theme.colors.primary : theme.colors["surface-raised"],
        borderWidth: done ? 0 : 2,
        borderColor: theme.colors.border,
    }),
}))
